import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { api, fileUrl, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Flag, Check, ShieldOff } from "lucide-react";

export default function AdminReports() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("open");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/reports", { params: { status: tab } });
      setRows(data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (user?.role === "admin") load(); /* eslint-disable-next-line */ }, [user, tab]);

  if (!user) return null;
  if (user.role !== "admin") return <Navigate to="/dashboard" replace />;

  const resolve = async (id) => {
    try { await api.post(`/admin/reports/${id}/resolve`); toast.success("Resolved"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const ban = async (uid) => {
    try { await api.post(`/admin/users/${uid}/ban`); toast.success("User banned"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-12 py-12 md:py-16" data-testid="admin-reports-page">
      <Flag className="h-7 w-7 text-[#C85A44] mb-4" strokeWidth={1.25}/>
      <p className="overline mb-3">Admin · Reports</p>
      <h1 className="font-serif-display text-4xl md:text-5xl tracking-tight">User reports.</h1>

      <div className="mt-8 flex gap-1 p-1 rounded-full bg-[#F2EFE9] w-fit">
        {["open", "resolved"].map((k) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`report-tab-${k}`}
            className={`px-5 py-2 rounded-full text-sm capitalize transition ${tab === k ? "bg-white shadow-sm" : "text-[#8C857B]"}`}>{k}</button>
        ))}
      </div>

      {loading ? <div className="text-center py-24 text-[#8C857B]">Loading…</div> :
       rows.length === 0 ? (
        <div className="text-center py-24 mt-6 border border-dashed border-[#E2DCD0] rounded-2xl text-[#8C857B]">No {tab} reports.</div>
      ) : (
        <div className="mt-8 space-y-3">
          {rows.map((r) => {
            const rep = r.reported;
            const by = r.reporter;
            const cover = rep?.photos?.[0] ? fileUrl(rep.photos[0]) : null;
            return (
              <div key={r.id} data-testid={`report-${r.id}`} className="p-5 rounded-2xl bg-white border border-[#E2DCD0]">
                <div className="flex items-center gap-4">
                  <div className="relative w-12 h-12 rounded-xl overflow-hidden bg-[#F2EFE9] flex-shrink-0">
                    {cover ? <img src={cover} alt="" className="absolute inset-0 w-full h-full object-cover"/> :
                      <div className="absolute inset-0 flex items-center justify-center font-serif-display text-[#8C857B]">{rep?.full_name?.[0]||"?"}</div>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm">
                      Reported: <Link to={`/profile/${r.reported_id}`} className="font-serif-display text-lg hover:text-[#C85A44]">{rep?.full_name || r.reported_id}</Link>
                      <span className="ml-2 text-xs bg-[#FAE5E0] text-[#9C3A3A] px-2 py-0.5 rounded-full">{r.reason}</span>
                    </div>
                    <div className="text-xs text-[#8C857B] mt-1">
                      by <span className="font-medium text-[#5d574f]">{by?.full_name || r.reporter_id}</span>
                      {r.at ? ` · ${new Date(r.at).toLocaleString()}` : ""}
                    </div>
                    {r.details && <div className="text-xs text-[#5d574f] mt-1 italic">"{r.details}"</div>}
                  </div>
                  <div className="flex gap-2">
                    {tab === "open" && <>
                      <button onClick={() => ban(r.reported_id)} data-testid={`report-ban-${r.id}`}
                        className="rounded-full bg-[#FAE5E0] hover:bg-[#F5D2C9] text-[#9C3A3A] px-3 py-1.5 text-xs inline-flex items-center gap-1">
                        <ShieldOff className="h-3 w-3"/> Ban
                      </button>
                      <button onClick={() => resolve(r.id)} data-testid={`report-resolve-${r.id}`}
                        className="rounded-full bg-[#E8F0EA] hover:opacity-90 text-[#2E5C46] px-3 py-1.5 text-xs inline-flex items-center gap-1">
                        <Check className="h-3 w-3"/> Resolve
                      </button>
                    </>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
